/**
 * @file LanguageDemandReport.tsx
 * @brief Super Admin report — language-pair demand (composes analytics API).
 *
 * @references
 * - features/analytics/api.ts (useLanguageStats)
 * - docs/product/reporting.md (sa-language-demand)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Languages, ListOrdered } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import { useLanguageStats } from "@/features/analytics/api";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import {
  formatDurationSeconds,
} from "@/shared/utils/formatters";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";
import { LanguagePairCell } from "@/features/reports-shared/LanguagePairCell";
import { mergeLanguagePairs } from "@/features/reports-shared/languagePairs";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
} from "@/features/reports-shared/previousRange";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function LanguageDemandReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const [companyId, setCompanyId] = useState<string | null>(null);

  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      companyId: companyId ?? undefined,
      limit: 100,
    }),
    [range.startDate, range.endDate, companyId],
  );

  const report = useLanguageStats(queryRange);
  // The same-length window before, for the +/-% pill (03-10-2026).
  const prevQueryRange = useMemo(
    () => ({ ...queryRange, ...previousRange(queryRange) }),
    [queryRange],
  );
  const prevReport = useLanguageStats(prevQueryRange);
  const rows = useMemo(() => mergeLanguagePairs(report.data ?? []), [report.data]);
  const totalSessions = rows.reduce(
    (s, r) => s + Number(r.session_count || 0),
    0,
  );

  return (
    <ReportShell
      title="Language demand"
      description="Top interpretation language pairs by session volume (up to 100)."
      range={range}
      onRangeChange={setRange}
      showCompanyFilter
      companyId={companyId}
      onCompanyChange={setCompanyId}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "language-demand",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          companyId: companyId ?? undefined,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Language pairs"
              icon={ListOrdered}
              value={String(rows.length)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Sessions"
              icon={Languages}
              value={String(totalSessions)}
              hint={previousRangeLabel(queryRange)}
              delta={
                prevReport.data
                  ? percentChange(
                      totalSessions,
                      prevReport.data.reduce((n, r) => n + Number(r.session_count || 0), 0),
                    )
                  : undefined
              }
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Language pairs"
          description={`${totalSessions} sessions across ${rows.length} pairs in range.`}
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Pair",
              { label: "Sessions", align: "right" },
              { label: "Duration", align: "right" },
              { label: "Share", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row, idx) => {
              const share =
                totalSessions > 0
                  ? Math.round(
                      (Number(row.session_count || 0) / totalSessions) * 1000,
                    ) / 10
                  : 0;
              return (
                <tr
                  key={`${row.source_language}-${row.target_language}-${idx}`}
                  className="hover:bg-secondary/20"
                >
                  <td className="px-4 py-2">
                    <LanguagePairCell
                      source={row.source_language}
                      target={row.target_language}
                    />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.session_count}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatDurationSeconds(row.total_duration_seconds)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {share}%
                  </td>
                </tr>
              );
            })}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
